import type { NumericFilter } from "../../utils/Api";

/**
 * Chip-label formatting for the numeric constraints the backend parsed out of
 * the search query (Search V3 epic #516; typed-query-first UX #536).
 *
 * The backend `GET /search` takes only `q`/`limit`/`offset` and parses numeric
 * constraints *out of the query string* (`api/api/search/constraints.py`, #522),
 * echoing the applied constraints back in `SearchResponse.numeric_filters`. The
 * typed query is the single source of truth — there is no separate frontend
 * filter model — so this module only needs to render each echoed constraint as a
 * human-readable chip label.
 */

/**
 * Human label for an applied numeric-constraint chip, built from the backend
 * echo (authoritative canonical `display`/`comparator`/`unit`), e.g.
 * "Glass transition temperature 140–200 °C" or "Tensile modulus ≥ 3 GPa".
 */
export const formatFilterLabel = (echo: NumericFilter): string => {
  const unit = echo.unit ? ` ${echo.unit}` : "";
  const lo = echo.value_min;
  const hi = echo.value_max;
  switch (echo.comparator) {
    case "range":
      // A range normally carries both bounds; degrade gracefully to a one-sided
      // label rather than printing "null" if an echo ever omits one.
      if (lo == null) return `${echo.display} ≤ ${hi}${unit}`;
      if (hi == null) return `${echo.display} ≥ ${lo}${unit}`;
      return `${echo.display} ${lo}–${hi}${unit}`;
    case "ge":
      return `${echo.display} ≥ ${lo}${unit}`;
    case "gt":
      return `${echo.display} > ${lo}${unit}`;
    case "le":
      return `${echo.display} ≤ ${hi}${unit}`;
    case "lt":
      return `${echo.display} < ${hi}${unit}`;
    default:
      return echo.display;
  }
};
